import { fetchMonthlyRevenue } from '../api/records';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer} from 'recharts';
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from 'react-i18next';


// Helpers 
const formatNarrow = (monthStr) => {
        const [year, month] = monthStr.split('-');
        return new Date(year, month-1).toLocaleString('default', {month: 'narrow'});
};
const formatLong = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return new Date(year, month-1).toLocaleString('default', {month: 'long', year: '2-digit'});
};
const formatShort = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return new Date(year, month-1).toLocaleString('default', {month: 'short', year: '2-digit'});
};

// Sub-components 
const CustomToolTip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className='bg-primary-800 border border-primary-50 rounded-lg p-2'>
            <p className='text-white text-sm'>{formatLong(payload[0].payload.originalMonth)}</p>
            <p className='text-white text-sm font-bold'>${payload[0].value}</p>
        </div>
    );
};

// Main component
const EarningsBarChart = () => {
    const { t } = useTranslation();

    const [filter, setFilter] = useState('6months');

    const {data: monthly_revenue = [], isLoading} = useQuery({
            queryKey: ['monthly-revenue'],
            queryFn: fetchMonthlyRevenue
    });
    
    const formattedRevenue = monthly_revenue.map(data => ({
        ...data,
        originalMonth: data.month,
        month: filter === '6months' ? formatShort(data.month) : formatNarrow(data.month)
    }));
    
    const filteredRevenue = filter === '6months'
        ? formattedRevenue.slice(-6)
        : formattedRevenue.slice(-12);
    

    return (
        <div className='mb-4'>
            {/* Header */}
            <p className="text-primary-50 text-xs font-mono mb-1">{t('main.monthly_earnings_title')}</p>

            <div className='bg-primary-800 rounded-2xl p-4'>
                {/* Current month total */}
                <div className='mb-4'>
                    <p className='text-primary-50 text-xs font-mono mb-1'>{t('main.current_month_label')}</p>
                    {isLoading ? (
                        <p className='text-white text-3xl font-bold'>-</p>
                    ) : (
                        <p className='text-white text-3xl font-bold'>${monthly_revenue.at(-1)?.total ?? 0}</p>
                    )}
                </div>

                {/* Filter toggle */}
                <div className='flex items-center gap-2 mb-4'>
                    {['6months', '1year'].map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={filter === f 
                                ? 'text-secondary-800 bg-secondary-50 rounded-lg px-2 py-1 text-xs font-mono font-bold' 
                                : 'text-primary-50 text-xs font-mono px-2 py-1'}>
                            {f === '6months' ? '6M' : '1Y'}
                        </button>
                    ))}
                    <p className="text-primary-50 text-xs font-mono">{filter === '6months' ? t('main.6months_label') : t('main.12months_label')}</p>
                </div>

                {/* Chart */}
                {isLoading ? (
                    <div className='flex items-center justify-center h-[300px]'>
                        <p className='text-primary-50 text-xs font-mono'>{t('general.loading')}</p>
                    </div>
                ): (
                <ResponsiveContainer width="100%" height={300}>
                <BarChart
                data={filteredRevenue}>
                    <XAxis dataKey="month" 
                    tick={{ fill: '#9ca3af', fontSize: 11}} />
                    <Tooltip content={<CustomToolTip />}/>
                    <Bar 
                    dataKey="total" 
                    fill='#4E6EF2'
                    radius={6}
                    />
                </BarChart>
            </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export default EarningsBarChart;